/**
 * Court Management Page - Modern Design
 */

import { useState } from 'react'
import { Search, Scale, Edit, Trash2, Plus, X } from 'lucide-react'
import {
  useCourts,
  useCreateCourt,
  useUpdateCourt,
  useDeleteCourt,
} from '@/hooks/useUsers'
import { toast } from '@/lib/toast'
import type { CourtCreate } from '@/types/user'

export function CourtManagement() {
  const [search, setSearch] = useState('')
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [editCourtId, setEditCourtId] = useState<number | null>(null)
  const [deleteCourtId, setDeleteCourtId] = useState<number | null>(null)
  const [currentPage, setCurrentPage] = useState(1)
  const itemsPerPage = 20

  // Fetch data
  const { data: courts, isLoading, error, refetch } = useCourts()

  // Mutations
  const createCourt = useCreateCourt()
  const updateCourt = useUpdateCourt()
  const deleteCourt = useDeleteCourt()

  // Form state
  const [formData, setFormData] = useState<CourtCreate>({
    court: '',
    code: '',
  })

  // Filter courts
  const filteredCourts = courts?.filter(court =>
    search === '' ||
    court.court.toLowerCase().includes(search.toLowerCase()) ||
    court.code.toLowerCase().includes(search.toLowerCase())
  )

  // Calculate pagination
  const totalPages = Math.ceil((filteredCourts?.length || 0) / itemsPerPage)
  const startIndex = (currentPage - 1) * itemsPerPage
  const endIndex = startIndex + itemsPerPage
  const paginatedCourts = filteredCourts?.slice(startIndex, endIndex)

  // Reset to page 1 when search changes
  const handleSearchChange = (value: string) => {
    setSearch(value)
    setCurrentPage(1)
  }

  // Handle edit
  const handleEdit = (courtId: number) => {
    const court = courts?.find(c => c.id === courtId)
    if (!court) return

    setFormData({
      court: court.court,
      code: court.code,
    })
    setEditCourtId(courtId)
  }

  // Handle submit
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    try {
      if (editCourtId) {
        await updateCourt.mutateAsync({ id: editCourtId, data: formData })
        toast.success('Court updated successfully')
        setEditCourtId(null)
      } else {
        await createCourt.mutateAsync(formData)
        toast.success('Court created successfully')
        setShowCreateModal(false)
      }
      resetForm()
      refetch()
    } catch (error: any) {
      toast.error(editCourtId ? 'Failed to update court' : 'Failed to create court', error.message)
    }
  }

  // Handle delete
  const handleDelete = async () => {
    if (deleteCourtId === null) return

    try {
      await deleteCourt.mutateAsync(deleteCourtId)
      toast.success('Court deleted successfully')
      setDeleteCourtId(null)
      refetch()
    } catch (error: any) {
      toast.error('Failed to delete court', error.message)
    }
  }

  const resetForm = () => {
    setFormData({
      court: '',
      code: '',
    })
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="bg-white p-8 rounded-xl shadow-lg text-center max-w-md">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Scale className="w-8 h-8 text-red-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Error Loading Courts</h2>
          <p className="text-gray-600 mb-6">{error.message}</p>
          <button
            onClick={() => refetch()}
            className="bg-primary text-white px-6 py-3 rounded-lg hover:bg-primary/90 transition-colors font-medium"
          >
            Try Again
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-primary flex items-center gap-3">
            <Scale className="h-8 w-8" />
            Court Management
          </h1>
          <p className="text-gray-600 mt-1">
            Manage court information and locations
          </p>
        </div>
        <button
          onClick={() => {
            resetForm()
            setShowCreateModal(true)
          }}
          className="bg-primary hover:bg-primary/90 text-white px-6 py-3 rounded-lg flex items-center gap-2 transition-colors shadow-sm font-medium"
        >
          <Plus className="w-5 h-5" />
          Add Court
        </button>
      </div>

      {/* Search Card */}
      <div className="bg-white rounded-xl border shadow-sm p-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => handleSearchChange(e.target.value)}
            placeholder="Search by court name or code..."
            className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
          />
        </div>
      </div>

      {/* Results Summary */}
      {filteredCourts && filteredCourts.length > 0 && (
        <div className="text-sm text-gray-600">
          Showing <span className="font-semibold text-gray-900">{startIndex + 1}</span> to{' '}
          <span className="font-semibold text-gray-900">{Math.min(endIndex, filteredCourts.length)}</span> of{' '}
          <span className="font-semibold text-gray-900">{filteredCourts.length}</span> courts
          {search && ` (filtered from ${courts?.length || 0} total)`}
        </div>
      )}

      {/* Table Card */}
      <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
        {isLoading ? (
          <div className="p-12 text-center">
            <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-primary border-r-transparent mb-4"></div>
            <p className="text-gray-600">Loading courts...</p>
          </div>
        ) : filteredCourts?.length === 0 ? (
          <div className="p-12 text-center">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Scale className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No courts found</h3>
            <p className="text-gray-600 mb-6">
              {search ? 'Try adjusting your search' : 'Create your first court to get started'}
            </p>
            <button
              onClick={() => setShowCreateModal(true)}
              className="bg-primary hover:bg-primary/90 text-white px-6 py-3 rounded-lg transition-colors"
            >
              Create First Court
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Court Name
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Code
                  </th>
                  <th className="px-6 py-4 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {paginatedCourts?.map((court) => (
                  <tr key={court.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4">
                      <span className="font-medium text-gray-900">{court.court}</span>
                    </td>
                    <td className="px-6 py-4">
                      <code className="px-2 py-1 bg-gray-100 rounded text-sm font-medium">
                        {court.code}
                      </code>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => handleEdit(court.id)}
                          className="inline-flex items-center gap-1 text-primary hover:text-primary/80 font-medium transition-colors"
                          title="Edit Court"
                        >
                          <Edit className="w-4 h-4" />
                          <span className="text-sm">Edit</span>
                        </button>

                        <button
                          onClick={() => setDeleteCourtId(court.id)}
                          className="inline-flex items-center gap-1 text-red-600 hover:text-red-700 font-medium transition-colors"
                          title="Delete Court"
                        >
                          <Trash2 className="w-4 h-4" />
                          <span className="text-sm">Delete</span>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
            <div className="text-sm text-gray-600">
              Page {currentPage} of {totalPages}
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setCurrentPage(currentPage - 1)}
                disabled={currentPage === 1}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Previous
              </button>
              <button
                onClick={() => setCurrentPage(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      {(showCreateModal || editCourtId) && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
            <div className="p-6 border-b flex items-center justify-between">
              <h3 className="text-xl font-bold text-gray-900">
                {editCourtId ? 'Edit Court' : 'Create New Court'}
              </h3>
              <button
                onClick={() => {
                  setShowCreateModal(false)
                  setEditCourtId(null)
                  resetForm()
                }}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Court Name *
                </label>
                <input
                  type="text"
                  required
                  value={formData.court}
                  onChange={(e) => setFormData({ ...formData, court: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  placeholder="e.g., Regional Trial Court - Branch 1"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Court Code *
                </label>
                <input
                  type="text"
                  required
                  value={formData.code}
                  onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  placeholder="e.g., RTC-BR1"
                />
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="submit"
                  className="flex-1 bg-primary hover:bg-primary/90 text-white px-4 py-2 rounded-lg transition-colors font-medium"
                >
                  {editCourtId ? 'Update Court' : 'Create Court'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateModal(false)
                    setEditCourtId(null)
                    resetForm()
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteCourtId !== null && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-2">Delete Court</h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete this court? This action cannot be undone.
            </p>
            <div className="flex gap-3">
              <button
                onClick={handleDelete}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg transition-colors font-medium"
              >
                Delete
              </button>
              <button
                onClick={() => setDeleteCourtId(null)}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-medium"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default CourtManagement
